import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../../../packages/data-grid";
import {FreeShippingFeesManagementDetachedCustomersDataGridColumnNames, PriceMatricesManagementExceptionalCustomersDataGrid} from "../../../../../../core/constants/table/data-grid-column-keys";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import IconButton from "../../../../../components/base/button/icon_button";
import {OpenInNew} from "@mui/icons-material";
import Api from "../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import SearchInput from "../../../../../components/base/input/search_input";
import PartnerDataGrid from "../../../../../containers/data-grid";
import useSearch from "../../../../../hooks/use-search";
import {PriceMatricesManagementExceptionalEntitiesDataGridActionNames} from "../index";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {Link} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const exportingChunkSize = 50;

const dataGridSaveConfig = {
    name: 'PriceMatricesManagementViewExceptionalCustomersViewDataGrid',
    version: 1,
}

const PriceMatricesManagementViewExceptionalCustomersView = () => {
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridControllerRef = useRef();
    const {configureNotebookTags} = useConfigureNotebook('price-matrices-exceptional-customers-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.priceMatrixCustomers);
        configureBehavioralTool((tags) => tags.priceMatrixCustomers);
    }, [configureNotebookTags, configureBehavioralTool]);

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                // {
                //     name: DataGridExoticColumnFields.selection,
                //     alignment: DataGridColumnAlignments.center,
                //     type: DataGridColumnTypes.element,
                //     width: 0,
                //     resizable: false,
                //     visibilityToggleable: false,
                //     reorderable: false,
                //     sortable: false,
                // },
                ...(operatorAccessLevels.packages.base.customerDetails ? [
                    {
                        name: PriceMatricesManagementExceptionalCustomersDataGrid.navigate,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        resizable: false,
                        visibilityToggleable: false,
                        reorderable: false,
                        sortable: false,
                        width: 0,
                    },
                ] : []),
                // {
                //     name: PriceMatricesManagementExceptionalCustomersDataGrid.remove,
                //     alignment: DataGridColumnAlignments.center,
                //     type: DataGridColumnTypes.element,
                //     resizable: false,
                //     visibilityToggleable: false,
                //     reorderable: false,
                //     sortable: false,
                //     width: 0,
                // },
                {
                    title: "Customer",
                    name: PriceMatricesManagementExceptionalCustomersDataGrid.customer,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                },
                {
                    title: "Custom Price matrices",
                    name: PriceMatricesManagementExceptionalCustomersDataGrid.customPriceMatrices,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.string,
                    width: 160,
                },
            ],
        [operatorAccessLevels.packages.base.customerDetails])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    /**
     * Creates the request body for the api call
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        const sorts = [];
        if (!!args.orderBy?.field) {
            sorts.push({
                isDesc: args.orderBy.descending,
                Property: args.orderBy.field,
            })
        }
        return {
            pagination: args.paginationInfo,
            keyword: args.filters?.keyword?.toString() ?? null,
            sorts: sorts,
        }
    }, [])


    /**
     * Gets the list of entries based on the filters from the user.
     * If the function is being called for export, the pagination info is passed as the input
     *
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.getPriceMatrixExceptionalCustomers(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareApiRequest])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)
    const canExportPriceMatrices = operatorAccessLevels.packages.sales.exportPriceMatrices;

    /**
     * Searches over list of entities in the system by setting the query params according to the received keyword
     * @param keyword
     */
    const searchWithKeywords = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword ?? "",
        });
    }, [searchData]);

    /**
     * Exports the list of records.
     * Calls the function for formatting the data
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag)
                return;

            if (!res?.data?.items?.length)
                break;

            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        if (!entries.length)
            return;
        return {
            title: 'Tier pricing Exceptional Customers List',
            body: entries.map((e, i) => ({
                '': i + 1,
                'Customer': e.name ?? '',
                'Custom Price Matrices': e.priceMatrixCount ?? '',
            }))
        }
    }, [fetchEntries, isMounted, searchData])

    /**
     * Calls the api regarding removing the list of detached customers form the list
     * @param exceptions list of exceptions associated to the customers to be removed
     * @private
     */
    const reattachCustomers = useCallback(async (exceptions) => {
        searchData.setLoading(true);
        for (const exception of exceptions) {
            // TODO: call api for reattaching customer to price matrices rules
            const response = await Api.removeFreightCustomerException(exception.id);
            if (!isMounted())
                return;
            if (!!response?.resultFlag) {
                setData(prevState => prevState.filter(e => exception.id !== e.id));
                searchData.setPaginationInfo((paginationInfo) => ({
                    ...paginationInfo,
                    length: paginationInfo.length - 1,
                }))
            }
        }
        if (!isMounted())
            return;
        dataGridControllerRef.current?.resetSelection();
        searchData.setLoading(false);
    }, [isMounted, searchData])


    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = useCallback((itemsToBeRemoved) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`All of the custom rules associated with the selected customer${itemsToBeRemoved !== 1 ? 's' : ""} will be overridden and lost. Do you wish to continue?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && reattachCustomers(itemsToBeRemoved),
        );
    }, [reattachCustomers])


    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return data?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [PriceMatricesManagementExceptionalCustomersDataGrid.navigate]:
                    <IconButton
                        color={'secondary'}
                        component={Link}
                        to={routeFunctions.customer.priceMatrix(item.id)}
                        target={'_blank'}
                    >
                        <OpenInNew className='icon normal'/>
                    </IconButton>,
                [FreeShippingFeesManagementDetachedCustomersDataGridColumnNames.remove]:
                    <IconButton
                        color={'paper.error'}
                        onPressed={() => getRemoveConfirmation([item])}
                    >
                        <RemoveIcon className={'icon normal'}/>
                    </IconButton>,
                [PriceMatricesManagementExceptionalCustomersDataGrid.customer]: item.name,
                [PriceMatricesManagementExceptionalCustomersDataGrid.customPriceMatrices]: item.priceMatrixCount,
            }
        }))
    }, [data, getRemoveConfirmation])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: PriceMatricesManagementExceptionalEntitiesDataGridActionNames.Delete,
                title: 'Remove custom price matrices',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    /**
     * Fetches all the customers given the available length.
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAllCustomers = useCallback(async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: 20,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                continue;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res?.data?.items ?? []);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }, [fetchEntries, searchData, isMounted])

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = useCallback(async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case PriceMatricesManagementExceptionalEntitiesDataGridActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows.map(e => e.data.customerId));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(data.map(e => e.customerId));
                }
                getRemoveConfirmation((await getAllCustomers(pagination.length)).map(e => e.customerId));
                break;
            }
            default:
                break;
        }
    }, [data, getAllCustomers, getRemoveConfirmation])

    return (
        <>
            <div className={'freight-companies-list-view'}>
                <p className={'text-sm mb-5'}>
                    List of customers who have custom set of tier pricing rules
                </p>
                <SearchInput
                    loading={searchData.loading}
                    initialValue={searchData.filters?.keyword}
                    autoSearch
                    immediateSearch
                    search={searchWithKeywords}
                    label={'Search'}
                />
                <div className={'mb-3'}/>
                <PartnerDataGrid
                    ref={dataGridControllerRef}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    // footerActions={footerActions}
                    // hideFooterActions={data?.length < 1}
                    // onActionInvoked={onActionInvoked}
                    visibleRows={data.length}
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    getExportInfo={canExportPriceMatrices ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
        </>
    );
}

export default PriceMatricesManagementViewExceptionalCustomersView;
